import React from "react";

export default function BlogEngineeringVsCaVsMedical({ onBack, onConsultationClick }) {
  return (
    <article className="min-h-screen bg-gradient-to-b from-blue-50 via-white to-purple-50 py-20 px-6">
      <div className="max-w-3xl mx-auto">
        <button
          onClick={onBack}
          className="mb-6 text-indigo-600 hover:underline font-semibold text-sm"
        >
          ← Back to Blog
        </button>

        <header className="mb-10">
          <h1 className="text-4xl md:text-5xl font-extrabold text-gray-900 leading-tight">
            Engineering vs CA vs Medical — Which Career Path Is Right for You?
          </h1>
          <p className="mt-4 text-gray-500 text-sm">
            Published by <span className="font-semibold text-indigo-600">Systematic Career Planner</span> · Updated May 2026 · 12 min read
          </p>
        </header>

        <img
          src="https://images.unsplash.com/photo-1434030216411-0b793f4b4173?w=800&q=80"
          alt="Students comparing career options"
          className="w-full rounded-2xl shadow-lg mb-10"
        />

        <section className="prose prose-lg max-w-none text-gray-700 space-y-8">
          <h2 className="text-2xl font-bold text-gray-800">The Big Three — India's Most Popular Career Choices</h2>
          <p>
            After 12th standard, three career paths dominate the aspirations of Indian students and parents:
            Engineering, Medicine, and Chartered Accountancy. But which one is truly right for <em>you</em>?
            At <strong>Systematic Career Planner</strong>, we believe the answer depends on
            your aptitude, interests, and long-term goals — not societal pressure.
          </p>

          {/* Engineering Section */}
          <h2 className="text-2xl font-bold text-gray-800">🛠️ Engineering (B.Tech / B.E.)</h2>

          <h3 className="text-xl font-semibold text-indigo-700">Who Is It For?</h3>
          <p>
            Students who enjoy mathematics, logical thinking, problem-solving, and have curiosity about
            technology, machines, and systems. If you loved Maths and Physics in school, engineering could
            be a natural fit.
          </p>

          <h3 className="text-xl font-semibold text-indigo-700">Key Branches &amp; Career Scope</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm border border-gray-200 rounded-lg">
              <thead className="bg-indigo-50">
                <tr>
                  <th className="text-left p-3 font-semibold">Branch</th>
                  <th className="text-left p-3 font-semibold">Top Roles</th>
                  <th className="text-left p-3 font-semibold">Avg Salary (Fresher)</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                <tr><td className="p-3">Computer Science / IT</td><td className="p-3">Software Developer, Data Scientist, AI Engineer</td><td className="p-3">₹5–15 LPA</td></tr>
                <tr><td className="p-3">Mechanical</td><td className="p-3">Design Engineer, Manufacturing, Automotive</td><td className="p-3">₹3–8 LPA</td></tr>
                <tr><td className="p-3">Electronics &amp; Comm.</td><td className="p-3">Embedded Systems, VLSI, Telecom</td><td className="p-3">₹4–10 LPA</td></tr>
                <tr><td className="p-3">Civil</td><td className="p-3">Structural Engineer, Urban Planner</td><td className="p-3">₹3–7 LPA</td></tr>
              </tbody>
            </table>
          </div>

          <h3 className="text-xl font-semibold text-indigo-700">Pros &amp; Cons</h3>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="bg-green-50 rounded-xl p-4">
              <p className="font-bold text-green-700 mb-2">✅ Pros</p>
              <ul className="list-disc list-inside space-y-1 text-sm">
                <li>Wide range of specializations</li>
                <li>High demand in IT/Tech sector</li>
                <li>Global opportunities &amp; remote work</li>
                <li>Multiple MBA/MS pathways after B.Tech</li>
              </ul>
            </div>
            <div className="bg-red-50 rounded-xl p-4">
              <p className="font-bold text-red-700 mb-2">❌ Cons</p>
              <ul className="list-disc list-inside space-y-1 text-sm">
                <li>Oversaturated in some branches</li>
                <li>Quality varies hugely across colleges</li>
                <li>Not all branches guarantee high salaries</li>
                <li>4-year commitment + competitive exams (JEE)</li>
              </ul>
            </div>
          </div>

          {/* Medical Section */}
          <h2 className="text-2xl font-bold text-gray-800">⚕️ Medical (MBBS / BDS)</h2>

          <h3 className="text-xl font-semibold text-indigo-700">Who Is It For?</h3>
          <p>
            Students passionate about biology, healthcare, and helping people. Medicine requires deep
            commitment, patience, and years of study — but rewards with immense professional satisfaction
            and social respect.
          </p>

          <h3 className="text-xl font-semibold text-indigo-700">Career Pathways</h3>
          <ul className="list-disc list-inside space-y-2">
            <li><strong>MBBS + MD/MS</strong> — Specialist doctor (Cardiology, Orthopedics, Dermatology, etc.)</li>
            <li><strong>BDS</strong> — Dentistry &amp; Oral Surgery</li>
            <li><strong>BAMS / BHMS</strong> — Ayurvedic / Homeopathic medicine</li>
            <li><strong>Allied Sciences</strong> — Physiotherapy, Nursing, Lab Technology</li>
            <li><strong>Research</strong> — Biomedical research, Pharma R&amp;D</li>
          </ul>

          <h3 className="text-xl font-semibold text-indigo-700">Pros &amp; Cons</h3>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="bg-green-50 rounded-xl p-4">
              <p className="font-bold text-green-700 mb-2">✅ Pros</p>
              <ul className="list-disc list-inside space-y-1 text-sm">
                <li>Recession-proof career</li>
                <li>High social respect &amp; impact</li>
                <li>Specialists earn very well (₹15–50+ LPA)</li>
                <li>Can practice independently</li>
              </ul>
            </div>
            <div className="bg-red-50 rounded-xl p-4">
              <p className="font-bold text-red-700 mb-2">❌ Cons</p>
              <ul className="list-disc list-inside space-y-1 text-sm">
                <li>Very long study duration (5.5 + 3 years for specialization)</li>
                <li>Extremely competitive (NEET)</li>
                <li>High stress &amp; demanding work hours</li>
                <li>Expensive private college fees</li>
              </ul>
            </div>
          </div>

          {/* CA Section */}
          <h2 className="text-2xl font-bold text-gray-800">📊 Chartered Accountancy (CA)</h2>

          <h3 className="text-xl font-semibold text-indigo-700">Who Is It For?</h3>
          <p>
            Students with strong analytical skills and interest in finance, accounting, auditing, and taxation.
            CA is ideal for those who are detail-oriented and enjoy working with numbers.
          </p>

          <h3 className="text-xl font-semibold text-indigo-700">CA Journey</h3>
          <div className="bg-purple-50 rounded-xl p-6 border-l-4 border-purple-500">
            <ol className="list-decimal list-inside space-y-2">
              <li><strong>CA Foundation</strong> — Entry after 12th (4 papers)</li>
              <li><strong>CA Intermediate</strong> — 8 papers in 2 groups</li>
              <li><strong>3 Years Articleship</strong> — Practical training under a CA</li>
              <li><strong>CA Final</strong> — 8 papers, become a qualified CA</li>
            </ol>
            <p className="mt-3 text-sm text-purple-700">Total duration: ~4.5–5 years after 12th</p>
          </div>

          <h3 className="text-xl font-semibold text-indigo-700">Pros &amp; Cons</h3>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="bg-green-50 rounded-xl p-4">
              <p className="font-bold text-green-700 mb-2">✅ Pros</p>
              <ul className="list-disc list-inside space-y-1 text-sm">
                <li>High earning potential (₹7–25+ LPA)</li>
                <li>Can start immediately after 12th</li>
                <li>Less expensive — no college fees</li>
                <li>Can practice independently or join top firms</li>
              </ul>
            </div>
            <div className="bg-red-50 rounded-xl p-4">
              <p className="font-bold text-red-700 mb-2">❌ Cons</p>
              <ul className="list-disc list-inside space-y-1 text-sm">
                <li>Very low pass rates (especially Final)</li>
                <li>Requires exceptional self-discipline</li>
                <li>Long study hours alongside articleship</li>
                <li>Multiple attempts are common</li>
              </ul>
            </div>
          </div>

          {/* Comparison Table */}
          <h2 className="text-2xl font-bold text-gray-800">Side-by-Side Comparison</h2>
          <div className="overflow-x-auto">
            <table className="w-full text-sm border border-gray-200 rounded-lg">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left p-3 font-semibold">Factor</th>
                  <th className="text-left p-3 font-semibold">Engineering</th>
                  <th className="text-left p-3 font-semibold">Medical</th>
                  <th className="text-left p-3 font-semibold">CA</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                <tr><td className="p-3 font-medium">Duration</td><td className="p-3">4 years</td><td className="p-3">5.5–8.5 years</td><td className="p-3">4.5–5 years</td></tr>
                <tr><td className="p-3 font-medium">Entrance Exam</td><td className="p-3">JEE Main/Advanced</td><td className="p-3">NEET</td><td className="p-3">CA Foundation</td></tr>
                <tr><td className="p-3 font-medium">Cost</td><td className="p-3">₹4–15 lakhs</td><td className="p-3">₹10–80 lakhs</td><td className="p-3">₹1–2 lakhs</td></tr>
                <tr><td className="p-3 font-medium">Starting Salary</td><td className="p-3">₹3–15 LPA</td><td className="p-3">₹5–12 LPA</td><td className="p-3">₹7–15 LPA</td></tr>
                <tr><td className="p-3 font-medium">Work-Life Balance</td><td className="p-3">Moderate–Good</td><td className="p-3">Challenging</td><td className="p-3">Moderate</td></tr>
                <tr><td className="p-3 font-medium">Global Scope</td><td className="p-3">Very High</td><td className="p-3">Moderate</td><td className="p-3">High</td></tr>
              </tbody>
            </table>
          </div>

          <h2 className="text-2xl font-bold text-gray-800">How to Decide — The SCP Method</h2>
          <div className="bg-indigo-50 rounded-xl p-6 border-l-4 border-indigo-500">
            <ol className="list-decimal list-inside space-y-2">
              <li><strong>Assess your aptitude</strong> — Are you a problem-solver, a caregiver, or an analyst?</li>
              <li><strong>Check your interest</strong> — Passion sustains you through years of hard work</li>
              <li><strong>Evaluate your financial situation</strong> — Medicine is expensive; CA is affordable</li>
              <li><strong>Think long-term</strong> — Where do you see yourself in 10 years?</li>
              <li><strong>Get expert guidance</strong> — Don't leave this decision to chance</li>
            </ol>
          </div>

          <div className="bg-pink-50 rounded-xl p-6 mt-8 text-center">
            <p className="text-lg font-bold text-gray-800 mb-2">Confused Between Engineering, Medical &amp; CA?</p>
            <p className="text-gray-600 mb-4">
              Our career experts can help you make the right choice based on your aptitude and goals.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              {/* <a href="#contact" className="px-6 py-3 rounded-full bg-indigo-600 text-white font-semibold hover:bg-indigo-700 transition">
                📅 Book Free Consultation
              </a> */}
               <button onClick={onConsultationClick} className="px-6 py-3 rounded-full bg-indigo-600 text-white font-semibold hover:bg-indigo-700 transition">
                📅 Book Free Consultation
              </button>
              <a
                href="https://wa.me/919920405421?text=Hi%2C%20I%20need%20help%20choosing%20between%20Engineering%2C%20Medical%20and%20CA"
                target="_blank"
                rel="noopener noreferrer"
                className="px-6 py-3 rounded-full bg-green-500 text-white font-semibold hover:bg-green-600 transition"
              >
                💬 WhatsApp Us
              </a>
            </div>
          </div>
        </section>
      </div>
    </article>
  );
}
